/**
 * Global popup pause gate.
 *
 * Two independent layers — a popup is hidden if EITHER is active:
 *   1. Device pause  — localStorage, only THIS browser (demo-safe, never
 *      touches live-visitor conversions). Also triggered by `?nopopups=1`.
 *   2. Global pause  — server flag (site_settings.popup_pause) that hides
 *      popups for ALL visitors. Polled from `/api/popups/pause-state`.
 *
 * Every popup component calls `usePopupsPaused()` (React) or the sync
 * `arePopupsPaused()` helper and renders nothing while paused.
 */
import { useEffect, useState } from 'react';
import { BACKEND_URL } from './api';

const DEVICE_KEY = 'wts_popups_paused_until'; // ISO string or 'forever'
const EVENT = 'wts:popup-pause-changed';

// Module-level cache of the server global state
let _globalPaused = false;
let _globalUntil = null;
let _polling = false;

function emitChange() {
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (e) { /* noop */ }
}

// ── URL param: ?nopopups=1 pins a device pause for this browser ──
function applyUrlOverride() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('nopopups') === '1' && !isDevicePaused()) {
      pauseOnThisDevice(0); // forever (until manually resumed)
    }
  } catch (e) { /* noop */ }
}

// ── Device pause helpers ──
export function isDevicePaused() {
  try {
    const v = localStorage.getItem(DEVICE_KEY);
    if (!v) return false;
    if (v === 'forever') return true;
    const until = new Date(v).getTime();
    if (isNaN(until)) return false;
    if (Date.now() >= until) {
      localStorage.removeItem(DEVICE_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

export function getDevicePauseUntil() {
  try {
    const v = localStorage.getItem(DEVICE_KEY);
    return v && v !== 'forever' ? v : (v === 'forever' ? 'forever' : null);
  } catch (e) {
    return null;
  }
}

export function pauseOnThisDevice(minutes) {
  try {
    if (!minutes || minutes <= 0) {
      localStorage.setItem(DEVICE_KEY, 'forever');
    } else {
      localStorage.setItem(DEVICE_KEY, new Date(Date.now() + minutes * 60000).toISOString());
    }
    emitChange();
  } catch (e) { /* noop */ }
}

export function resumeOnThisDevice() {
  try {
    localStorage.removeItem(DEVICE_KEY);
    emitChange();
  } catch (e) { /* noop */ }
}

// ── Global (server) pause helpers ──
export function isGlobalPaused() {
  return _globalPaused;
}

export function getGlobalState() {
  return { paused: _globalPaused, paused_until: _globalUntil };
}

export async function fetchGlobalPauseState() {
  try {
    const r = await fetch(`${BACKEND_URL}/api/popups/pause-state`);
    if (!r.ok) return;
    const j = await r.json();
    const next = !!j.paused;
    if (next !== _globalPaused || j.paused_until !== _globalUntil) {
      _globalPaused = next;
      _globalUntil = j.paused_until || null;
      emitChange();
    }
  } catch (e) { /* network blip — keep last known state */ }
}

// ── Combined gate ──
export function arePopupsPaused() {
  return isDevicePaused() || _globalPaused;
}

/**
 * Start polling the server pause state. Safe to call multiple times —
 * only one poller runs. Mount once at app root.
 */
export function startPopupPausePolling(intervalMs = 60000) {
  applyUrlOverride();
  fetchGlobalPauseState();
  if (_polling) return;
  _polling = true;
  setInterval(fetchGlobalPauseState, intervalMs);
}

/**
 * React hook — re-renders the consumer whenever device OR global pause
 * state changes. Returns the combined boolean.
 */
export function usePopupsPaused() {
  const [paused, setPaused] = useState(arePopupsPaused());
  useEffect(() => {
    const update = () => setPaused(arePopupsPaused());
    update();
    window.addEventListener(EVENT, update);
    // Catch device-pause timers expiring + cross-tab changes
    const id = setInterval(update, 15000);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(EVENT, update);
      window.removeEventListener('storage', update);
      clearInterval(id);
    };
  }, []);
  return paused;
}
